export const MAX_NOTIFICATION_ATTEMPTS_REACHED_CODE = "MAX_NOTIFICATION_ATTEMPTS_REACHED" as const;

const DEFAULT_MAX_ATTEMPTS_MESSAGE = "Maximum notification attempts reached.";

const sendNotificationResponseCodes = [MAX_NOTIFICATION_ATTEMPTS_REACHED_CODE] as const;
export type SendNotificationErrorResponseCode = (typeof sendNotificationResponseCodes)[number];

// Parsed from an untrusted 403 response body — fields are optional because a
// malformed or unexpected error shape may omit them. Narrow before relying on them.
export interface SendNotificationErrorResponse {
    code?: SendNotificationErrorResponseCode;
    message?: string;
}

/**
 * Thrown when send-notification refuses a further send because the session already hit its
 * attempt limit (HTTP 403 with body `{ code: "MAX_NOTIFICATION_ATTEMPTS_REACHED", ... }`).
 * Mirrors the backend's `MaxNotificationAttemptsError`, so the UI can show the real
 * "maximum number of attempts" message instead of the generic "please try again".
 *
 * Keying on the body's `code` rather than the bare 403 status matters: an authorizer or
 * gateway 403 (expired token, policy violation) is NOT an attempt-limit refusal, and telling
 * an expert they hit a send limit they never hit sends them looking for the wrong problem.
 */
export class MaxNotificationAttemptsError extends Error {
    readonly code = MAX_NOTIFICATION_ATTEMPTS_REACHED_CODE;
    readonly statusCode = 403;

    constructor(message: string = DEFAULT_MAX_ATTEMPTS_MESSAGE, options?: ErrorOptions) {
        super(message, options);
        this.name = "MaxNotificationAttemptsError";
        // Keep `instanceof` reliable across module/transpilation boundaries.
        Object.setPrototypeOf(this, MaxNotificationAttemptsError.prototype);
    }
}

/**
 * True when the backend refused a further send because the per-session attempt cap is spent.
 *
 * Callers branch on this to show the cap-specific copy AND to suppress `sendSalesErrorMetric`.
 * The suppression is the load-bearing part: a cap refusal is a business rule, not a fault. It is
 * already counted server-side by
 * `exwo_SendNotificationRequest_total{notificationOutcome="MAX_ATTEMPTS_REACHED"}`, and a second
 * error-shaped counter on the frontend feeds `sell-error-rate-frontend`, which counts
 * exwo_Error_total by {surface,component} with NO errorType filter — so it cannot be excluded by
 * type. That double-count paged on-call at ~200/day for expected behavior (ew#7426).
 */
export function isMaxNotificationAttemptsError(error: unknown): error is MaxNotificationAttemptsError {
    return error instanceof MaxNotificationAttemptsError;
}
